import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { colors } from '@/utils/colors';
import { VIRTUAL_CLUBS, type ClubHeadShape } from '@/physics/swingModel';

/**
 * Procedural club geometry — the meshes `ClubOverlay` hangs on the swinging
 * controller. Everything is authored in the overlay's canonical frame: origin
 * at the grip butt, shaft down -Z, face normal +X, heel toward +Y for a
 * right-hander. Head groups have their ORIGIN AT THE FACE CENTRE — the impact
 * point the swing controls sweep through the ball — so the physics contract
 * survives every cosmetic upgrade made here.
 *
 * The load-bearing decision is the loft architecture. Each head group holds
 * two siblings:
 *
 *  - `hoselAssembly` — FIXED, never rotates. Ferrule + tapered hosel + neck,
 *    with the upper section exactly coaxial with the shaft axis (x=0, y=0), so
 *    the shaft junction is welded by construction at every loft.
 *  - `loftGroup` — carries body + face + grooves + details with the shape's
 *    NOMINAL loft pre-baked into the geometry. `applyLoft` rotates the group
 *    only by the small residual (actual − nominal), about the local Y axis
 *    through the origin. The face centre lies ON that axis, so the impact
 *    point is invariant under both the bake and the residual rotation — and
 *    because the hosel never moves at all, loft can never reopen the old
 *    "wedge head shears off the shaft" gap.
 */

/**
 * The loft baked into each shape's geometry: the midpoint of that shape's loft
 * range in VIRTUAL_CLUBS (wood 12.75°, iron 34°, wedge 51° today), computed by
 * scanning the table so a re-tuned bag re-centres the bake automatically.
 * Residual rotations then stay small — see MAX_LOFT_RESIDUAL_DEG.
 */
const NOMINAL_LOFT: Record<ClubHeadShape, number> = (() => {
  const min: Partial<Record<ClubHeadShape, number>> = {};
  const max: Partial<Record<ClubHeadShape, number>> = {};
  for (const club of VIRTUAL_CLUBS) {
    min[club.head] = Math.min(min[club.head] ?? club.loft, club.loft);
    max[club.head] = Math.max(max[club.head] ?? club.loft, club.loft);
  }
  const mid = (shape: ClubHeadShape) => ((min[shape] ?? 0) + (max[shape] ?? 0)) / 2;
  return { wood: mid('wood'), iron: mid('iron'), wedge: mid('wedge') };
})();

/**
 * Beyond this residual the geometry still works (nothing can detach), but the
 * body starts to look visibly tipped relative to its fixed hosel — a sign that
 * VIRTUAL_CLUBS changed shape ranges and the bake midpoints drifted. Warn so
 * the regression is caught in the console, not on someone's head-mounted club.
 */
const MAX_LOFT_RESIDUAL_DEG = 12;

/**
 * Heel → face-centre lateral offset per shape, from VIRTUAL_CLUBS (uniform per
 * shape there). The SHAFT LINE passes through head-local
 * `y = +heelSign · FACE_OFFSET[shape]` — the heel — while the head-group
 * origin stays at the face centre (the impact point). The overlay hangs each
 * head `FACE_OFFSET` toe-ward off the drawn shaft so the in-head neck lands
 * exactly back on the shaft axis.
 */
export const FACE_OFFSET: Record<ClubHeadShape, number> = (() => {
  const offset: Partial<Record<ClubHeadShape, number>> = {};
  for (const club of VIRTUAL_CLUBS) offset[club.head] ??= club.faceOffset;
  return { wood: offset.wood ?? 0.044, iron: offset.iron ?? 0.038, wedge: offset.wedge ?? 0.036 };
})();

const { degToRad } = THREE.MathUtils;

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------

export type ClubMaterials = {
  shaft: THREE.Material;
  grip: THREE.Material;
  ferrule: THREE.Material;
  hosel: THREE.Material;
  crown: THREE.Material;
  sole: THREE.Material;
  face: THREE.Material;
  body: THREE.Material;
  detail: THREE.Material;
  accent: THREE.Material;
};

/**
 * The solid (real club) material set. Caller owns disposal — the overlay's
 * traverse-dispose remains the material owner, the builders here never
 * dispose materials. Deliberately never handed to `WorldMask.applyToMaterial`:
 * the club lives in the player's hands inside the passthrough bay.
 */
export function makeSolidMaterials(options: {
  shaftColor?: THREE.ColorRepresentation;
  headColor?: THREE.ColorRepresentation;
} = {}): ClubMaterials {
  const std = (color: THREE.ColorRepresentation, metalness: number, roughness: number) =>
    new THREE.MeshStandardMaterial({ color, metalness, roughness });
  return {
    shaft: std(options.shaftColor ?? 0xd8dee0, 0.9, 0.25),
    grip: std(0x17191b, 0.02, 0.92),
    ferrule: std(0x101114, 0.10, 0.45),
    hosel: std(0xc4cbd0, 0.85, 0.30),
    crown: std(0x14181c, 0.35, 0.30),
    sole: std(options.headColor ?? 0xcfd6da, 0.85, 0.25),
    face: std(0xd8dde1, 0.80, 0.32),
    body: std(0xc9ced3, 0.75, 0.38),
    detail: std(0x2a2e33, 0.40, 0.60),
    accent: new THREE.MeshStandardMaterial({
      color: colors.primary,
      emissive: new THREE.Color(colors.primary),
      emissiveIntensity: 0.35,
      metalness: 0.2,
      roughness: 0.5,
    }),
  };
}

/**
 * The ghost (alignment guide) material set. An outline has to survive bright
 * passthrough without reading as a second club: translucent, no depth writes
 * or tests (drawn over the world), DoubleSide so mirrored/openEnded geometry
 * never vanishes. Instances are shared across slots where the look is the
 * same — double-dispose of a shared material is harmless.
 */
export function makeGhostMaterials(): ClubMaterials {
  const ghost = (color: THREE.ColorRepresentation, opacity: number) =>
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthWrite: false,
      depthTest: false,
      fog: false,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
  const primary = ghost(colors.primary, 0.32);
  const white = ghost(colors.white, 0.55);
  return {
    shaft: ghost(colors.primary, 0.5),
    grip: white,
    ferrule: white,
    hosel: primary,
    crown: primary,
    sole: primary,
    face: ghost(colors.white, 0.6),
    body: primary,
    // Unused by ghost builds (detail:'ghost' omits grooves and the crown dot),
    // but the type is total so the slots reuse the body tint.
    detail: primary,
    accent: primary,
  };
}

// ---------------------------------------------------------------------------
// Small shared utilities
// ---------------------------------------------------------------------------

/**
 * The club must never be cut away by the MR portal mask — it lives in the
 * player's hands, inside the passthrough bay. `traverse` includes the root.
 */
function markExempt(root: THREE.Object3D) {
  root.traverse(child => { child.userData.mrMaskExempt = true; });
}

const v2 = ([r, y]: readonly [number, number]) => new THREE.Vector2(r, y);

const MIRROR_Y = new THREE.Matrix4().makeScale(1, -1, 1);

/**
 * Mirror handed geometry across the Y=0 plane for a left-handed build. NEVER
 * done with `scale.y = -1` on a group — that flips triangle winding at render
 * time and the head draws inside-out. Instead: reflect the geometry
 * (`applyMatrix4` transforms normals via the normal matrix, so they come out
 * correctly mirrored), then restore front-face winding by swapping the second
 * and third vertex of every triangle. Run AFTER the loft bake, as the final
 * geometry step, so it mirrors exactly what will render.
 */
function mirrorGeometryY(geo: THREE.BufferGeometry) {
  geo.applyMatrix4(MIRROR_Y);
  const index = geo.getIndex();
  if (index) {
    for (let i = 0; i < index.count; i += 3) {
      const b = index.getX(i + 1);
      index.setX(i + 1, index.getX(i + 2));
      index.setX(i + 2, b);
    }
    index.needsUpdate = true;
  } else {
    // Non-indexed: swap vertices B and C of each triangle across ALL
    // attributes (position, normal, uv, …) so they stay in lockstep.
    const triCount = geo.getAttribute('position').count / 3;
    for (const attr of Object.values(geo.attributes) as THREE.BufferAttribute[]) {
      const size = attr.itemSize;
      const array = attr.array;
      for (let tri = 0; tri < triCount; tri++) {
        const b = (tri * 3 + 1) * size;
        const c = (tri * 3 + 2) * size;
        for (let k = 0; k < size; k++) {
          const tmp = array[b + k];
          array[b + k] = array[c + k];
          array[c + k] = tmp;
        }
      }
      attr.needsUpdate = true;
    }
  }
}

/**
 * Orientation pipeline for every extruded part. Extrudes are authored in
 * shape space — x = toe-heel with HEEL POSITIVE, y = face-up, extrusion depth
 * along +z. Two quarter turns permute that basis cyclically onto the head
 * frame: shape-x → head +Y, shape-y → head +Z, depth → head +X. The front
 * bevel cap then lands at max x, and a bounding-box shift plants it exactly on
 * the face plane (x = `frontX`), so extrudes always present a true flat face.
 */
function orientExtrudeToFace(geo: THREE.BufferGeometry, frontX = 0) {
  geo.rotateX(Math.PI / 2);
  geo.rotateZ(Math.PI / 2);
  geo.computeBoundingBox();
  geo.translate(-geo.boundingBox!.max.x + frontX, 0, 0);
}

/** Rounded-rectangle Shape centred on the origin — the driver's face insert. */
function roundedRect(width: number, height: number, radius: number): THREE.Shape {
  const hw = width / 2;
  const hh = height / 2;
  const shape = new THREE.Shape();
  shape.moveTo(-hw + radius, -hh);
  shape.lineTo(hw - radius, -hh);
  shape.absarc(hw - radius, -hh + radius, radius, -Math.PI / 2, 0, false);
  shape.lineTo(hw, hh - radius);
  shape.absarc(hw - radius, hh - radius, radius, 0, Math.PI / 2, false);
  shape.lineTo(-hw + radius, hh);
  shape.absarc(-hw + radius, hh - radius, radius, Math.PI / 2, Math.PI, false);
  shape.lineTo(-hw, -hh + radius);
  shape.absarc(-hw + radius, -hh + radius, radius, Math.PI, Math.PI * 1.5, false);
  return shape;
}

// ---------------------------------------------------------------------------
// Shaft + grip (canonical frame: origin at the butt, down -Z)
// ---------------------------------------------------------------------------

/**
 * Unit-length shaft along -Z from the origin; the overlay stretches it butt →
 * head with `scale.z`. The taper survives that stretch: the radii live in
 * X/Y, untouched by a Z scale, and a linear taper profile stays linear under a
 * linear stretch — which is also why there are no baked-in decorative rings
 * (they would smear into ovals at driver length). Open-ended: the butt end
 * hides inside the grip's open taper and the tip inside the ferrule. ~24 tris.
 */
export function buildShaft(materials: ClubMaterials): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(0.0062, 0.0045, 1, 12, 1, true)
    .rotateX(-Math.PI / 2) // cylinder +Y → -Z: thick tip end reaches the hosel
    .translate(0, 0, -0.5);
  const mesh = new THREE.Mesh(geo, materials.shaft);
  mesh.name = 'ClubShaft';
  markExempt(mesh);
  return mesh;
}

/** Nominal grip length, metres — the end lip of the lathe profile below. */
const GRIP_LENGTH = 0.27;

/**
 * Grip profile as (radius, distance-down-shaft-from-butt) pairs. One closed
 * lathe — cap, lip, two ring grooves and the long taper — so the whole grip is
 * a single draw call (~470 tris) instead of a stack of cylinders. Ordered by
 * ascending y: LatheGeometry derives its normals from the profile tangent, so
 * an ascending profile points them outward.
 */
const GRIP_PROFILE: ReadonlyArray<readonly [number, number]> = [
  [0.0000, 0.000], // butt cap centre — radius 0 closes the cap
  [0.0110, 0.001],
  [0.0152, 0.004], // cap lip
  [0.0148, 0.010],
  [0.0140, 0.030],
  [0.0134, 0.034], // ring groove 1…
  [0.0140, 0.038],
  [0.0132, 0.072], // …ring groove 2
  [0.0138, 0.076],
  [0.0125, 0.160],
  [0.0112, 0.240],
  [0.0106, 0.266],
  [0.0100, GRIP_LENGTH], // end lip
  [0.0060, GRIP_LENGTH + 0.001], // open-end taper swallows the shaft junction
];

/**
 * Fixed-size grip, butt at the origin, running down -Z. Unlike the shaft it
 * is never scaled — hands are the same size at every club length.
 */
export function buildGrip(materials: ClubMaterials): THREE.Group {
  const geo = new THREE.LatheGeometry(GRIP_PROFILE.map(v2), 16)
    .rotateX(-Math.PI / 2); // profile +y (down-shaft) → -Z
  const mesh = new THREE.Mesh(geo, materials.grip);
  mesh.name = 'ClubGripSleeve';
  const group = new THREE.Group();
  group.name = 'ClubGripParts';
  group.add(mesh);
  markExempt(group);
  return group;
}

// ---------------------------------------------------------------------------
// Hosel assembly parts (FIXED — never rotate with loft)
// ---------------------------------------------------------------------------

/**
 * Ferrule profile as (radius, head-local z) — positive z is up-shaft from the
 * face centre. Ascending order for outward lathe normals; both ends open (the
 * shaft passes through the top, the hosel meets the bottom at r 0.0064).
 */
const FERRULE_PROFILE: ReadonlyArray<readonly [number, number]> = [
  [0.0064, 0.076],
  [0.0070, 0.079],
  [0.0072, 0.086],
  [0.0060, 0.090],
];

/** The little trim collar where shaft meets hosel. ~72 tris. Sits ON the shaft
 *  line — i.e. at the HEEL, FACE_OFFSET away from the face-centre origin. */
function buildFerrule(shape: ClubHeadShape, materials: ClubMaterials, heelSign: 1 | -1): THREE.Mesh {
  const geo = new THREE.LatheGeometry(FERRULE_PROFILE.map(v2), 12)
    .rotateX(Math.PI / 2) // lathe +Y → head +Z (up-shaft)
    .translate(0, heelSign * FACE_OFFSET[shape], 0);
  const mesh = new THREE.Mesh(geo, materials.ferrule);
  mesh.name = 'ClubFerrule';
  return mesh;
}

/**
 * Neck centre-lines, authored RELATIVE to the shaft line: y is "toward the
 * face centre from the heel" and gets `FACE_OFFSET[shape] + y` (then heelSign)
 * at build time, so the upper points sit exactly on the shaft axis at the heel
 * and the lower points bow back and inward to dive into the body. In FINAL
 * (post-bake) head coordinates — the neck is fixed and never rotates with
 * loft. The wood's neck bends further inboard because it must reach through
 * the crown's falling curve before it can be buried.
 */
const NECK_LINES: Record<ClubHeadShape, ReadonlyArray<{ x: number; y: number; z: number; r: number }>> = {
  wood: [
    { x: 0.000, y: 0.000, z: 0.076, r: 0.0064 },
    { x: 0.000, y: 0.000, z: 0.042, r: 0.0068 },
    { x: -0.005, y: -0.008, z: 0.028, r: 0.0085 },
    { x: -0.008, y: -0.018, z: 0.014, r: 0.0105 }, // flared boss, buried in the crown
  ],
  iron: [
    { x: 0.000, y: 0.000, z: 0.078, r: 0.0064 },
    { x: 0.000, y: 0.000, z: 0.040, r: 0.0070 },
    { x: -0.004, y: -0.002, z: 0.024, r: 0.0074 },
    { x: -0.007, y: -0.005, z: 0.012, r: 0.0076 },
    { x: -0.009, y: -0.008, z: 0.004, r: 0.0078 },
  ],
  wedge: [
    { x: 0.000, y: 0.000, z: 0.078, r: 0.0064 },
    { x: 0.000, y: 0.000, z: 0.040, r: 0.0070 },
    { x: -0.004, y: -0.002, z: 0.024, r: 0.0074 },
    { x: -0.007, y: -0.004, z: 0.012, r: 0.0076 },
    { x: -0.009, y: -0.007, z: 0.002, r: 0.0078 },
  ],
};

/**
 * Stacked open-ended cylinder frustums aligned between consecutive centre-line
 * points, radii matched at the joins and each extended 1mm so the joins
 * overlap instead of gapping — a cheap swept tube (~100 tris) without a
 * TubeGeometry's segment cost. Ends stay open: the top hides inside the
 * ferrule, the bottom inside the body/heel pad.
 */
function buildNeck(shape: ClubHeadShape, materials: ClubMaterials, heelSign: 1 | -1): THREE.Mesh {
  const offset = FACE_OFFSET[shape];
  const line = NECK_LINES[shape].map(p => ({
    point: new THREE.Vector3(p.x, (offset + p.y) * heelSign, p.z),
    r: p.r,
  }));
  const up = new THREE.Vector3(0, 1, 0);
  const dir = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i]; // up-shaft end — rides the cylinder's +Y (radiusTop) side
    const b = line[i + 1];
    dir.subVectors(a.point, b.point);
    const length = dir.length();
    dir.normalize();
    const frustum = new THREE.CylinderGeometry(a.r, b.r, length + 0.001, 12, 1, true);
    quat.setFromUnitVectors(up, dir);
    frustum.applyQuaternion(quat);
    frustum.translate(
      (a.point.x + b.point.x) / 2,
      (a.point.y + b.point.y) / 2,
      (a.point.z + b.point.z) / 2,
    );
    parts.push(frustum);
  }
  const geo = mergeGeometries(parts);
  for (const part of parts) part.dispose();
  const mesh = new THREE.Mesh(geo, materials.hosel);
  mesh.name = 'ClubNeck';
  return mesh;
}

// ---------------------------------------------------------------------------
// Driver ('wood') loft parts — ~1160 tris
// ---------------------------------------------------------------------------

// The driver body is designed in the SQUARE frame — face plane = YZ through
// the origin, face normal +X, crown +Z — then the nominal loft is baked into
// every loftGroup geometry with rotateY(-nominal) as the last authored step.

/**
 * Crown and sole as lathe profiles (radius, z) around the body's vertical
 * axis, circular footprint Ø0.115. They share the skirt ring at
 * (0.0575, -0.006) so the surfaces meet exactly; being separate geometries
 * (and materials), their normals are computed separately — the shading crease
 * at the skirt is deliberate, it is what reads as "driver" at arm's length.
 * Both ascend in z for outward lathe normals (the sole profile runs from its
 * low apex up to the skirt).
 */
const WOOD_CROWN_PROFILE: ReadonlyArray<readonly [number, number]> = [
  [0.0575, -0.006], // skirt
  [0.0560, 0.004],
  [0.0540, 0.012],
  [0.0440, 0.022],
  [0.0300, 0.029],
  [0.0120, 0.0315],
  [0.0000, 0.032], // apex
];
const WOOD_SOLE_PROFILE: ReadonlyArray<readonly [number, number]> = [
  [0.0000, -0.030],
  [0.0280, -0.0295],
  [0.0480, -0.024],
  [0.0575, -0.006], // same skirt ring as the crown
];

/**
 * Where the body ball sits: behind the face, biased heel-ward (the mass of a
 * real driver hangs off the far side of the shaft), a hair above the face
 * centre. Right-handed value — a left-handed build mirrors the finished
 * geometry instead of flipping this sign, because by then the clamped lathes
 * are handed shapes.
 */
const WOOD_BODY_CENTRE = { x: -0.030, y: -0.018, z: 0.001 } as const;

/**
 * The lathes are a full ball; everything forward of this plane is squashed
 * flat so the body presents a face for the plate to sit on. 2mm behind the
 * origin: the plate (front cap at +0.002) straddles the face centre, keeping
 * the impact point on the visible face.
 */
const WOOD_FACE_CLAMP_X = -0.002;

/**
 * Flatten every vertex forward of the clamp plane, then recompute normals so
 * the flat face shades flat. Degenerate triangles are left in — they cost
 * nothing and removing them would mean re-indexing for no visible gain.
 */
function clampToFacePlane(geo: THREE.BufferGeometry) {
  const position = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < position.count; i++) {
    if (position.getX(i) > WOOD_FACE_CLAMP_X) position.setX(i, WOOD_FACE_CLAMP_X);
  }
  position.needsUpdate = true;
  geo.computeVertexNormals();
}

function buildWoodLoftParts(
  loftGroup: THREE.Group,
  materials: ClubMaterials,
  heelSign: 1 | -1,
  detail: 'full' | 'ghost',
) {
  const bake = -degToRad(NOMINAL_LOFT.wood);

  // Crown + sole shells: lathe → place → clamp → bake → mirror. The clamp has
  // to happen in the square frame (its plane IS the face plane), and the
  // mirror last, on the finished handed shape.
  const shell = (profile: ReadonlyArray<readonly [number, number]>) => {
    const geo = new THREE.LatheGeometry(profile.map(v2), 32)
      .rotateX(Math.PI / 2); // lathe +Y → head +Z: profiles were authored as (r, z)
    geo.translate(WOOD_BODY_CENTRE.x, WOOD_BODY_CENTRE.y, WOOD_BODY_CENTRE.z);
    clampToFacePlane(geo);
    geo.rotateY(bake);
    if (heelSign === -1) mirrorGeometryY(geo);
    return geo;
  };
  const crown = new THREE.Mesh(shell(WOOD_CROWN_PROFILE), materials.crown);
  crown.name = 'ClubCrown';
  const sole = new THREE.Mesh(shell(WOOD_SOLE_PROFILE), materials.sole);
  sole.name = 'ClubSole';
  loftGroup.add(crown, sole);

  // Face plate (~220 tris): a bevelled rounded-rect slab whose front cap sits
  // 2mm proud of the clamped body — the visible hitting surface. The rect is
  // y-symmetric, so its heel-ward centring is a plain heelSign placement (no
  // mirror needed — the mirrored placement IS the mirror image).
  const plate = new THREE.ExtrudeGeometry(roundedRect(0.096, 0.048, 0.013), {
    depth: 0.007,
    bevelEnabled: true,
    bevelThickness: 0.0015,
    bevelSize: 0.0015,
    bevelSegments: 2,
    curveSegments: 4,
  });
  orientExtrudeToFace(plate, 0.002);
  plate.translate(0, -0.012 * heelSign, -0.004);
  plate.rotateY(bake);
  const face = new THREE.Mesh(plate, materials.face);
  face.name = 'ClubFacePlate';
  loftGroup.add(face);

  // Alignment dot (~70 tris), half-sunk into the crown where a real driver
  // prints one. A sphere is symmetric, so heelSign placement suffices. Skipped
  // for the ghost — the outline reads better without micro-detail.
  if (detail === 'full') {
    const dotGeo = new THREE.SphereGeometry(0.003, 8, 5);
    dotGeo.translate(-0.014, -0.012 * heelSign, 0.0295);
    dotGeo.rotateY(bake);
    const dot = new THREE.Mesh(dotGeo, materials.accent);
    dot.name = 'ClubCrownDot';
    loftGroup.add(dot);
  }
}

// ---------------------------------------------------------------------------
// Iron / wedge loft parts — muscle-back blade (~860 / ~900 tris)
// ---------------------------------------------------------------------------

/**
 * Blade outlines in shape space (s, t): s = toe-heel with HEEL POSITIVE,
 * t = face-up, face centre at (0,0). Control points for a closed Catmull-Rom
 * — heel-bottom, along the sole to the toe, up and over the topline, down the
 * heel into the neck. Built right-handed; the finished geometry is mirrored
 * for left-handers.
 */
const IRON_OUTLINE: ReadonlyArray<readonly [number, number]> = [
  [0.030, -0.016],
  [0.034, -0.022],
  [-0.008, -0.026],
  [-0.036, -0.024],
  [-0.046, -0.014],
  [-0.049, 0.002],
  [-0.044, 0.016],
  [-0.030, 0.024],
  [-0.006, 0.027],
  [0.008, 0.0295],
  [0.016, 0.0295],
  [0.026, 0.020],
  [0.034, 0.004],
];

/**
 * The wedge is the iron recipe with deltas: taller blade (t ∈ [-0.030, 0.034]),
 * a fuller toe (apex held wide at (-0.047, +0.010)), and a topline that runs
 * nearly straight into the neck plateau at t +0.034.
 */
const WEDGE_OUTLINE: ReadonlyArray<readonly [number, number]> = [
  [0.030, -0.020],
  [0.034, -0.026],
  [-0.008, -0.030],
  [-0.036, -0.028],
  [-0.046, -0.018],
  [-0.047, 0.010], // toe apex — fuller than the iron's
  [-0.042, 0.024],
  [-0.028, 0.031],
  [-0.006, 0.033],
  [0.010, 0.034],
  [0.018, 0.034], // neck plateau
  [0.027, 0.024],
  [0.034, 0.002],
];

type BladeRecipe = {
  outline: ReadonlyArray<readonly [number, number]>;
  /** Extrude depth (face → back), metres. */
  depth: number;
  grooves: { rows: number; tStart: number; pitch: number; length: number };
  muscleBar: boolean;
  bounceSole: boolean;
};

const BLADE_RECIPES: Record<'iron' | 'wedge', BladeRecipe> = {
  iron: {
    outline: IRON_OUTLINE,
    depth: 0.0075,
    grooves: { rows: 11, tStart: -0.016, pitch: 0.0034, length: 0.050 },
    muscleBar: true,
    bounceSole: false,
  },
  wedge: {
    outline: WEDGE_OUTLINE,
    depth: 0.010,
    grooves: { rows: 13, tStart: -0.022, pitch: 0.004, length: 0.054 },
    muscleBar: false,
    bounceSole: true,
  },
};

/** The wedge's sole is rotated loft PLUS this much — the bounce angle. */
const WEDGE_BOUNCE_DEG = 12;

function buildBladeLoftParts(
  shape: 'iron' | 'wedge',
  loftGroup: THREE.Group,
  materials: ClubMaterials,
  heelSign: 1 | -1,
  detail: 'full' | 'ghost',
) {
  const recipe = BLADE_RECIPES[shape];
  const bake = -degToRad(NOMINAL_LOFT[shape]);

  // Blade: sample the closed Catmull-Rom into a polygon and extrude it with a
  // small bevel. mergeVertices welds the extrude's duplicated seam vertices so
  // computeVertexNormals can smooth across the bevel — that soft edge is the
  // whole "forged" look; without the weld every facet shades hard.
  const curve = new THREE.CatmullRomCurve3(
    recipe.outline.map(([s, t]) => new THREE.Vector3(s, t, 0)),
    true,
  );
  const outline = new THREE.Shape().setFromPoints(
    curve.getPoints(48).map(p => new THREE.Vector2(p.x, p.y)),
  );
  const extruded = new THREE.ExtrudeGeometry(outline, {
    depth: recipe.depth,
    bevelEnabled: true,
    bevelThickness: 0.0012,
    bevelSize: 0.0012,
    bevelSegments: 2,
    curveSegments: 1, // the outline is already sampled — no further subdivision
  });
  // mergeVertices hashes EVERY attribute, and ExtrudeGeometry bakes flat
  // per-facet normals and position-derived uvs — leave those in and nothing
  // welds, so the shading stays faceted. Nothing here is textured, so drop
  // both and weld purely by position.
  extruded.deleteAttribute('normal');
  extruded.deleteAttribute('uv');
  const blade = mergeVertices(extruded, 1e-4);
  extruded.dispose();
  blade.computeVertexNormals();
  orientExtrudeToFace(blade); // front cap onto the face plane x = 0
  blade.rotateY(bake);

  // Back details merge into the blade for one draw call. mergeGeometries needs
  // uniform indexing AND uniform attribute sets: extrudes are non-indexed
  // while boxes are indexed — so everything goes through toNonIndexed() first
  // (the blade too: it became indexed when mergeVertices welded it; its smooth
  // normals survive the expansion) — and the boxes shed the uv the blade no
  // longer carries (their crisp flat normals stay). Boxes are authored
  // directly in pre-bake head coordinates (s → Y, t → Z, depth → X) and baked
  // with the blade's rotation.
  const bodyParts: THREE.BufferGeometry[] = [blade.toNonIndexed()];
  blade.dispose();
  const boxPart = (box: THREE.BoxGeometry) => {
    box.deleteAttribute('uv');
    bodyParts.push(box.toNonIndexed());
    box.dispose();
  };

  if (recipe.muscleBar) {
    // Muscle bar: 0.052(s) × 0.006(depth) × 0.014(t), back face flush at
    // x = -0.0135 — it pierces the blade back (-0.0099) and carries the mass.
    const bar = new THREE.BoxGeometry(0.006, 0.052, 0.014);
    bar.translate(-0.0105, -0.006, -0.014);
    bar.rotateY(bake);
    boxPart(bar);
  }

  // Heel pad: 0.016(s) × 0.020(depth) × 0.020(t) hugging the back heel. Its
  // footprint (y 0.020–0.036, z -0.002–0.018, back to x -0.0215) blankets the
  // fixed neck's lower rear — which now dives in AT THE HEEL, around
  // y ≈ FACE_OFFSET − 0.008 — so the neck's open bottom end and its dive into
  // the blade are always concealed at any residual loft.
  const pad = new THREE.BoxGeometry(0.020, 0.016, 0.020);
  pad.translate(-0.0115, 0.028, 0.008);
  pad.rotateY(bake);
  boxPart(pad);

  if (recipe.bounceSole) {
    // Bounce sole: 0.075(s) × 0.018(depth) × 0.006 slab under the blade's
    // lower rear, top half embedded 3mm into the blade bottom (t -0.030),
    // front edge just behind the face plane so only the trailing flange shows.
    // Baked at loft PLUS 12° — the extra tilt relative to the blade IS the
    // bounce angle, presented sole-first the way a real wedge sits.
    const bounce = new THREE.BoxGeometry(0.018, 0.075, 0.006);
    bounce.translate(-0.010, -0.004, -0.030);
    bounce.rotateY(-degToRad(NOMINAL_LOFT.wedge + WEDGE_BOUNCE_DEG));
    boxPart(bounce);
  }

  const bodyGeo = mergeGeometries(bodyParts);
  for (const part of bodyParts) part.dispose();
  if (heelSign === -1) mirrorGeometryY(bodyGeo); // after the bake — final step
  const body = new THREE.Mesh(bodyGeo, materials.body);
  body.name = 'ClubBlade';
  loftGroup.add(body);

  // Grooves: thin boxes a hair proud of the face (x +0.0002), one merged draw
  // call. Built pre-bake so the rows are authored flat in (s, t), then baked
  // with the same rotation as the blade so they stay glued to its face.
  // Skipped for the ghost, which wants a clean outline, not scoring lines.
  if (detail === 'full') {
    const rows: THREE.BufferGeometry[] = [];
    for (let i = 0; i < recipe.grooves.rows; i++) {
      const row = new THREE.BoxGeometry(0.0006, recipe.grooves.length, 0.0008);
      row.translate(0.0002, -0.005, recipe.grooves.tStart + i * recipe.grooves.pitch);
      rows.push(row);
    }
    const groovesGeo = mergeGeometries(rows); // all indexed boxes — merge as-is
    for (const row of rows) row.dispose();
    groovesGeo.rotateY(bake);
    if (heelSign === -1) mirrorGeometryY(groovesGeo);
    const grooves = new THREE.Mesh(groovesGeo, materials.detail);
    grooves.name = 'ClubGrooves';
    loftGroup.add(grooves);
  }
}

// ---------------------------------------------------------------------------
// Head assembly
// ---------------------------------------------------------------------------

export type HeadBuild = {
  group: THREE.Group;
  /** Rotate the loftGroup to present this loft (degrees) — hosel stays put. */
  applyLoft(deg: number): void;
  /** Dispose geometries only — materials are caller-owned. */
  dispose(): void;
};

/**
 * Build one head, origin at the face centre (the overlay parks it at
 * z = -length). `hoselAssembly` is fixed and shaft-coaxial; `loftGroup`
 * carries everything the loft tips, with the nominal loft pre-baked so
 * `applyLoft` only ever applies a small residual about the face centre.
 */
export function buildHead(
  shape: ClubHeadShape,
  materials: ClubMaterials,
  heelSign: 1 | -1,
  detail: 'full' | 'ghost' = 'full',
): HeadBuild {
  const group = new THREE.Group();
  group.name = `ClubHead_${shape}`;

  const hoselAssembly = new THREE.Group();
  hoselAssembly.name = 'ClubHoselAssembly';
  hoselAssembly.add(buildFerrule(shape, materials, heelSign));
  hoselAssembly.add(buildNeck(shape, materials, heelSign));

  const loftGroup = new THREE.Group();
  loftGroup.name = 'ClubLoftGroup';

  if (shape === 'wood') {
    buildWoodLoftParts(loftGroup, materials, heelSign, detail);
  } else {
    buildBladeLoftParts(shape, loftGroup, materials, heelSign, detail);
  }

  group.add(hoselAssembly, loftGroup);
  markExempt(group);

  const nominal = NOMINAL_LOFT[shape];
  return {
    group,
    applyLoft(deg: number) {
      const residual = deg - nominal;
      if (Math.abs(residual) > MAX_LOFT_RESIDUAL_DEG) {
        // If VIRTUAL_CLUBS grew a loft range past the bake midpoints, the body
        // will look tipped off its fixed hosel long before anything detaches —
        // flag it here rather than letting the old gap bug creep back quietly.
        console.warn(
          `clubGeometry: ${deg}° loft is ${residual.toFixed(1)}° from the baked ` +
          `${nominal}° nominal for '${shape}' — did VIRTUAL_CLUBS change?`,
        );
      }
      loftGroup.rotation.y = -degToRad(residual);
    },
    dispose() {
      // Geometries only: the overlay's traverse-dispose owns the materials.
      group.traverse((child: any) => { child.geometry?.dispose?.(); });
    },
  };
}
